import { createPromiseTtlCache } from "@/lib/promiseTtlCache";
import { STYLIST_SERVICES, stylistPhotoKey } from "@/lib/stylists";
import { OpeningsJson } from "./openingsClient";

export type StylistPhotoCategories = Record<string, Record<string, string[]>>;

/** Read the actual portfolio categories, which profile endpoints omit. */
export async function getStylistPhotoCategories(
  businessId: string,
): Promise<StylistPhotoCategories> {
  const { items: categories } =
    await OpeningsJson.getPhotoCategories(businessId);
  const assignments: StylistPhotoCategories = {};
  await Promise.all(
    categories.map(async (category) => {
      const service = STYLIST_SERVICES.find(
        (item) =>
          item.label.toLowerCase() === category.name.trim().toLowerCase(),
      );
      if (!service) return;
      let cursor: string | undefined;
      const seenCursors = new Set<string>();
      do {
        const page = await OpeningsJson.getPhotos({
          businessId,
          category: category.id,
          limit: 100,
          ...(cursor ? { cursor } : {}),
        });
        for (const photo of page.items) {
          if (!photo.user?.id || !photo.src) continue;
          const member = (assignments[photo.user.id] ??= {});
          const services = (member[stylistPhotoKey(photo.src)] ??= []);
          if (!services.includes(service.id)) services.push(service.id);
        }
        cursor = page.nextCursor == null ? undefined : String(page.nextCursor);
        if (cursor && seenCursors.has(cursor))
          throw new Error("Photo category pagination did not advance");
        if (cursor) seenCursors.add(cursor);
      } while (cursor);
    }),
  );
  return assignments;
}

// Share in-flight reads across profiles; failed category reads remain retryable.
const categoryCache = createPromiseTtlCache<string, StylistPhotoCategories>({ ttlMs: 300_000 });
export const getCachedStylistPhotoCategories = (businessId: string) =>
  categoryCache.get(businessId, () => getStylistPhotoCategories(businessId));
